import React from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from './pagination';

export default function PaginationWrapper({
  activePage,
  itemsCountPerPage,
  totalItemsCount,
  pageRangeDisplayed = 5,
  onChange,
  ...props
}) {
  const totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);
  const currentPage = activePage || 1;

  // Determine page range based on screen size
  // For very small screens (≤480px), show fewer pages
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const effectivePageRange = isMobile ? Math.min(3, pageRangeDisplayed) : pageRangeDisplayed;

  // Calculate page range to display
  const getPageNumbers = () => {
    const pages = [];
    const halfRange = Math.floor(effectivePageRange / 2);
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust if we're near the start or end
    if (endPage - startPage < effectivePageRange - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + effectivePageRange - 1);
      } else {
        startPage = Math.max(1, endPage - effectivePageRange + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();
  const showFirstEllipsis = pageNumbers[0] > 2;
  const showLastEllipsis = pageNumbers[pageNumbers.length - 1] < totalPages - 1;

  if (totalPages <= 1) {
    return null;
  }

  return (
    <Pagination {...props}>
      <PaginationContent>
        {currentPage > 1 && (
          <PaginationItem>
            <PaginationPrevious
              onClick={(e) => {
                e.preventDefault();
                onChange(currentPage - 1);
              }}
              href="#"
            />
          </PaginationItem>
        )}

        {showFirstEllipsis && (
          <>
            <PaginationItem>
              <PaginationLink
                onClick={(e) => {
                  e.preventDefault();
                  onChange(1);
                }}
                href="#"
                isActive={currentPage === 1}
              >
                1
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          </>
        )}

        {pageNumbers.map((page) => (
          <PaginationItem key={page}>
            <PaginationLink
              onClick={(e) => {
                e.preventDefault();
                onChange(page);
              }}
              href="#"
              isActive={currentPage === page}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}

        {showLastEllipsis && (
          <>
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink
                onClick={(e) => {
                  e.preventDefault();
                  onChange(totalPages);
                }}
                href="#"
                isActive={currentPage === totalPages}
              >
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        {currentPage < totalPages && (
          <PaginationItem>
            <PaginationNext
              onClick={(e) => {
                e.preventDefault();
                onChange(currentPage + 1);
              }}
              href="#"
            />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  );
}

